<template>
  <layout>
    <template v-slot:header>
      <AppHead title="Inventory Order Lookup" />
      <h2 class="mb-2">Inventory Order From Products</h2>
      <BreadCrumbs activeItem="Inventory Shipments" :crumbs="{ home: 'Dashboard', inventory: 'Inventory' }"/>
      <flash-messages />
    </template>
    
    <template v-slot:default>
      <Search :searchText="searchText" @searched="search"/>
        <SelectButton v-model="searchStatus" :options="status" optionLabel="desc" optionValue="id" :multiple="false" @click="statusFilter">
        <template #option="slotProps">
          <div class="status-option">
            <div>{{slotProps.option.desc}}
              <span class="status-option-count">{{slotProps.option.count}}</span>
            </div>
          </div>
        </template>
        </SelectButton>
      
        <div class="row">
          <div class="col-sm-6">
            <label for="manufacturer_select" class="mb-0">Manufacturer: </label>
            <Multiselect
              v-model="manufacturer_selected"
              :options="manufacturers"
              :searchable="true"
              id="manufacturer_select"
              @change="manufacturer"
            />
          </div>
          <div class="col-sm-2">
            <label for="threshold" class="mb-0">In Stock # Threshold: </label>
            <input type="number" 
              v-model="threshold" 
              id="threshold"
              class="form-control"
              @input="search"
            />
          </div>
        </div>

        <DataTable ref="admin-inventory-products-table" 
          :value="productsLow" :paginator="true" :rows="100" 
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" removableSort
          paginatorPosition="both" currentPageReportTemplate="({first} - {last} of {totalRecords})"
          :rowsPerPageOptions="[10, 20, 50, 100]" 
          sortField="date" :sortOrder="-1"
          stateStorage="local" stateKey="admin-inventory-products" dataKey="id"
          class="p-datatable-sm admin-datatable"
          tableClass="border-left border-right border-bottom">
            <template #paginatorLeft>
              {{ inventoryOrderItems.length }} Items
            </template>
            <Column field="product_id" header="ID" :sortable="true"></Column>
            <Column field="sku" header="SKU" :sortable="true">
              <template #body="slotProps">
                  <span class="text-nowrap">{{ slotProps.data.sku }}</span>
              </template>
            </Column>
            <Column field="description" header="Description" :sortable="true"></Column>
            <Column field="current_inventory" header="Current" :sortable="true"></Column>
            <Column field="current_inventory_incoming" header="Incoming" :sortable="true"></Column>
            <Column field="current_inventory_outgoing" header="Outgoing" :sortable="true"></Column>
            <Column field="final_inventory" header="Final" :sortable="true"></Column>
            <Column field="open_inventory_order.created_at" header="Open PO" :sortable="true">
              <template #body="slotProps">
                <a v-if="slotProps.data.open_inventory_order" :href="route('inventory.view', slotProps.data.open_inventory_order.id)" target="_new">{{ slotProps.data.open_inventory_order.order_number }} {{ moment(slotProps.data.open_inventory_order.created_at).format('M/D/YY h:mm A') }}</a>
              </template>
            </Column>
            <Column field="last_inventory_order.created_at" header="Last PO" :sortable="true">
              <template #body="slotProps">
                <span v-if="slotProps.data.last_inventory_order" class="text-nowrap">
                  {{ moment(slotProps.data.last_inventory_order.created_at).format('M/D/YY h:mm A') }}
                </span>
                <span v-else>
                  Not Ordered
                </span>
              </template>
            </Column>
            <Column field="last_inventory_order_shipment.created_at" header="Shipment" :sortable="true">
              <template #body="slotProps">
                <span v-if="slotProps.data.last_inventory_order_shipment" class="text-nowrap">
                  {{ moment(slotProps.data.last_inventory_order_shipment.created_at).format('M/D/YY h:mm A') }}
                </span>
                <span v-else>
                  No Shipment
                </span>
              </template>
            </Column>

            <Column field="orders_recent" header="30/60/90" :sortable="true" sortField="Sold_30_Days">
              <template #body="slotProps">
                <span class="text-nowrap">{{ slotProps.data.Sold_30_Days }}/
                {{ slotProps.data.Sold_30_60_Days }}/
                {{ slotProps.data.Sold_60_90_Days }}</span>
              </template>
            </Column>
            <Column :exportable="false">
              <template #body="slotProps">
                <span class="text-nowrap">
                  <jet-button :goesTo="route('admin.inventory.addproduct', slotProps.data.product_id)" class="btn-sm" icon="fas fa-plus" tabindex="-1">Open PO</jet-button>
                  <jet-button class="btn-short text-nowrap mx-2" :goesTo="route('admin.products.view', slotProps.data.product_id)" icon="fas fa-chevron-right">View</jet-button>
                </span>
              </template>
            </Column>
        </DataTable>
    </template>
  </layout>
</template>

<script>
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Admin/Layout'
import SearchFilter from '@/Shared/SearchFilter'
import FlashMessages from '@/Shared/FlashMessages'
import Search from '@/Shared/Admin/Search'
import moment from 'moment'
import Helpers from '@/Helpers.js'
import Multiselect from '@vueform/multiselect'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import SelectButton from 'primevue/selectbutton';

export default {
  components: {
    Icon,
    SearchFilter,
    Layout,
    Multiselect,
    SelectButton,
    FlashMessages,
    Search,
    BreadCrumbs
  },
  props: {
    productsLow: Array,
    query: String,
    manufacturers: Array,
    categories: Array,
    status: Array,
    selectedStatus: Number,
    manufacturer_id: Number,
    filter_inventory_threshold: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      searchText: this.query,
      searchStatus: this.selectedStatus,
      categories_selected: [],
      manufacturer_selected: this.manufacturer_id,
      threshold: this.filter_inventory_threshold,
      filterOrderedNumber: null
    }
  },
  created() {
    this.moment = moment;
  },
  methods: {
    search(e, searchText) {
      this.$refs['admin-inventory-products-table'].d_sortField = null
      this.$refs['admin-inventory-products-table'].resetPage()
      this.$inertia.get(this.route('inventory.fromproducts'), {
        query: searchText,
        manufacturer_id: this.manufacturer_selected,
        filter_inventory_threshold: this.threshold
      })
    },
    category(selected) {
      // control gets this event before model is updated
      this.categories_selected = selected;
      this.search();
    },
    manufacturer(selected) {
      this.manufacturer_selected = selected;

      this.search();
    },
    statusFilter(e) {
      this.$refs['admin-inventory-table'].resetPage()
      this.$inertia.get(this.route('inventory.shipments'), {
        query: this.searchText,
        status: this.searchStatus,
        manufacturer_id: this.manufacturer,
      })
    },
    filterOrdered() {
      let threshold = this.filterOrderedNumber;

      this.products.each((product) => {
        if (product.inventoryInfo.outgoing_inventory - proudct.inventoryInfo.current_inventory <= threshold) {
          product.hide = false;
        } else {
          product.hide = true;
        }
      });
    },
    formatCurrency: Helpers.formatCurrency
  },
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
